import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

// Deterministic hash so node positions don't reshuffle every render.
function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function seededRandom(seed) {
  const x = Math.sin(seed * 9301 + 49297) * 49297;
  return x - Math.floor(x);
}

// Cluster volume tuned so the bubble cloud fills the frame nicely from z=16
// without spilling off-screen. Bubbles get a deterministic position inside a
// sphere of this radius.
const CLUSTER_RADIUS = 5;

function placeNodes(nodes) {
  return nodes.map((n) => {
    const h = hashStr(String(n.id));
    const u = seededRandom(h);
    const v = seededRandom(h + 1);
    const w = seededRandom(h + 2);
    const theta = u * Math.PI * 2;
    const phi = Math.acos(2 * v - 1);
    // cbrt distribution -> uniform volume; bias toward outer shell.
    const r = CLUSTER_RADIUS * Math.cbrt(0.2 + w * 0.8);
    return {
      ...n,
      position: [
        r * Math.sin(phi) * Math.cos(theta),
        r * Math.sin(phi) * Math.sin(theta),
        r * Math.cos(phi),
      ],
      seedOffset: (h % 1000) / 1000,
    };
  });
}

// Texture caches — keyed by URL / hex color so we don't re-decode or
// re-generate across renders.
const imageTextureCache = new Map();
const videoTextureCache = new Map();
const colorTextureCache = new Map();
let glossTextureSingleton = null;

// Shared gloss-overlay texture. Mostly transparent, with a sharp top-left
// specular, a faint bottom-right bounce, and a thin bright rim to suggest
// fresnel / sphere curvature. Overlaid on every bubble — color, photo,
// or video — so they all read as glossy glass orbs instead of matte discs.
function getGlossTexture() {
  if (glossTextureSingleton) return glossTextureSingleton;
  const size = 256;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d');
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 1;

  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.clip();

  // Primary specular — top-left, sharp, bright. This is what sells the
  // gloss; sharper falloff than before so it reads as shine, not haze.
  const hl1 = ctx.createRadialGradient(size * 0.32, size * 0.28, 0, size * 0.32, size * 0.28, size * 0.24);
  hl1.addColorStop(0, 'rgba(255,255,255,0.95)');
  hl1.addColorStop(0.25, 'rgba(255,255,255,0.55)');
  hl1.addColorStop(0.6, 'rgba(255,255,255,0.14)');
  hl1.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = hl1;
  ctx.fillRect(0, 0, size, size);

  // Secondary bounce — bottom-right, dim. Suggests light wrapping around
  // the back of the sphere.
  const hl2 = ctx.createRadialGradient(size * 0.72, size * 0.78, 0, size * 0.72, size * 0.78, size * 0.2);
  hl2.addColorStop(0, 'rgba(255,255,255,0.3)');
  hl2.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = hl2;
  ctx.fillRect(0, 0, size, size);

  // Fresnel rim — thin bright ring just inside the edge. Reads as the
  // refractive edge of a glass bubble.
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = 'rgba(255,255,255,0.42)';
  ctx.beginPath();
  ctx.arc(cx, cy, radius - 1.8, 0, Math.PI * 2);
  ctx.stroke();

  ctx.restore();

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = 4;
  glossTextureSingleton = tex;
  return tex;
}

// Solid-color bubble texture with a soft highlight and a thin anti-aliased
// edge. Pre-rendered once per category color (cached) so colorless bubbles
// read as crisp orbs, not blurry blobs.
function getColorTexture(hex) {
  if (colorTextureCache.has(hex)) return colorTextureCache.get(hex);
  const size = 256;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d');
  const col = new THREE.Color(hex);
  const r = Math.round(col.r * 255);
  const g = Math.round(col.g * 255);
  const b = Math.round(col.b * 255);
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 1;

  // Translucent body — like a glass bubble. The gloss overlay mesh adds
  // the highlight + rim on top, so the body only handles color & alpha.
  const body = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
  body.addColorStop(0, `rgba(${r},${g},${b},0.30)`);
  body.addColorStop(0.55, `rgba(${r},${g},${b},0.50)`);
  body.addColorStop(0.88, `rgba(${r},${g},${b},0.75)`);
  body.addColorStop(0.98, `rgba(${Math.round(r * 0.85)},${Math.round(g * 0.85)},${Math.round(b * 0.85)},0.9)`);
  body.addColorStop(1, `rgba(${r},${g},${b},0)`);
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = 4;
  tex.needsUpdate = true;
  colorTextureCache.set(hex, tex);
  return tex;
}

// Load a photo URL, center-crop it to a square canvas, upload as texture.
// One-time per URL; promise resolves with the texture.
function getImageTexture(url, onReady) {
  if (!url) return null;
  if (imageTextureCache.has(url)) {
    onReady && onReady(imageTextureCache.get(url));
    return imageTextureCache.get(url);
  }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const size = Math.min(img.naturalWidth, img.naturalHeight, 512);
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d');
    const s = Math.min(img.naturalWidth, img.naturalHeight);
    const sx = (img.naturalWidth - s) / 2;
    const sy = (img.naturalHeight - s) / 2;
    ctx.drawImage(img, sx, sy, s, s, 0, 0, size, size);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    imageTextureCache.set(url, tex);
    onReady && onReady(tex);
  };
  img.onerror = () => {
    imageTextureCache.delete(url);
  };
  img.src = url;
  return null;
}

// VideoTexture with UV crop applied so the video center-crops to a square
// without stretching, even if the source is 16:9 or 9:16.
function getVideoTexture(url) {
  if (!url) return null;
  if (videoTextureCache.has(url)) return videoTextureCache.get(url);
  const video = document.createElement('video');
  video.src = url;
  video.crossOrigin = 'anonymous';
  video.loop = true;
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;
  video.play().catch(() => {});
  const tex = new THREE.VideoTexture(video);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;
  // Once the video reports its dimensions, set UV repeat/offset to center-crop.
  const applyCrop = () => {
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    if (w > h) {
      const ratio = h / w;
      tex.repeat.set(ratio, 1);
      tex.offset.set((1 - ratio) / 2, 0);
    } else if (h > w) {
      const ratio = w / h;
      tex.repeat.set(1, ratio);
      tex.offset.set(0, (1 - ratio) / 2);
    }
  };
  video.addEventListener('loadedmetadata', applyCrop);
  applyCrop();
  videoTextureCache.set(url, tex);
  return tex;
}

// One memory bubble — a flat circle that always faces the camera. This means
// the photo or video texture is never stretched, regardless of camera angle.
// A second gloss-overlay mesh sits just in front of the body so everything
// reads as a glossy glass orb, not a matte sticker.
function MemoryBubble({ node, isSelected, isHovered, isHighlightedByAI, isSpeaking, isDimmed, onSelect, onHover }) {
  const groupRef = useRef();
  const ringRef = useRef();
  const { camera } = useThree();
  const glossTex = useMemo(() => getGlossTexture(), []);
  // Start every bubble with the color-fallback texture (always available
  // synchronously). Photos swap in via useEffect once the image has decoded
  // and been center-cropped. Videos use a VideoTexture that streams frames.
  const [texture, setTexture] = useState(() => {
    if (node.media_type === 'video' && node.media_url) {
      return getVideoTexture(node.media_url);
    }
    return getColorTexture(node.color || '#7FCDBB');
  });

  useEffect(() => {
    if (node.media_type === 'image' && (node.thumbnail_url || node.media_url)) {
      getImageTexture(node.thumbnail_url || node.media_url, (t) => setTexture(t));
    } else if (node.media_type === 'video' && node.media_url) {
      setTexture(getVideoTexture(node.media_url));
    } else {
      setTexture(getColorTexture(node.color || '#7FCDBB'));
    }
  }, [node.media_url, node.thumbnail_url, node.media_type, node.color]);

  // Bubble radius — smaller than before so the cluster doesn't feel crowded.
  const baseRadius = useMemo(() => node.size * 0.012 + 0.12, [node.size]);

  useFrame((state) => {
    if (!groupRef.current) return;
    const t = state.clock.elapsedTime;
    const off = node.seedOffset || 0;

    // Subtle bob so the cluster feels alive (not just spinning rigidly).
    groupRef.current.position.x = node.position[0] + Math.sin(t * 0.4 + off * 6.28) * 0.03;
    groupRef.current.position.y = node.position[1] + Math.sin(t * 0.3 + off * 6.28) * 0.025;
    groupRef.current.position.z = node.position[2] + Math.cos(t * 0.4 + off * 6.28) * 0.03;

    // Always face the camera — this is what kills the texture stretching.
    // quaternion.copy is ~5x cheaper than lookAt for camera-facing billboards.
    groupRef.current.quaternion.copy(camera.quaternion);

    // State-driven scale.
    let targetScale = 1;
    if (isSelected) targetScale = 1.5;
    else if (isHighlightedByAI && isSpeaking) targetScale = 1.3 + Math.sin(t * 6 + off * 6) * 0.08;
    else if (isHighlightedByAI) targetScale = 1.2;
    else if (isHovered) targetScale = 1.18;
    else if (isDimmed) targetScale = 0.85;

    groupRef.current.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.12);

    // Pulsing AI-reference ring.
    if (ringRef.current) {
      ringRef.current.position.copy(groupRef.current.position);
      ringRef.current.quaternion.copy(camera.quaternion);
      if (isHighlightedByAI) {
        const pulse = 1 + Math.sin(t * 4 + off * 6) * 0.18;
        ringRef.current.scale.set(targetScale * pulse, targetScale * pulse, 1);
        ringRef.current.material.opacity = isSpeaking ? 0.65 : 0.35;
      } else {
        ringRef.current.material.opacity = 0;
      }
    }
  });

  const handleOver = useCallback((e) => { e.stopPropagation(); onHover && onHover(node.id); document.body.style.cursor = 'pointer'; }, [node.id, onHover]);
  const handleOut = useCallback((e) => { e.stopPropagation(); onHover && onHover(null); document.body.style.cursor = 'auto'; }, [onHover]);
  const handleClick = useCallback((e) => { e.stopPropagation(); onSelect && onSelect(node); }, [node, onSelect]);

  const opacity = isDimmed ? 0.22 : 1.0;

  return (
    <>
      {/* Halo ring — only visible when this bubble is AI-referenced. */}
      <mesh ref={ringRef} position={node.position} visible={false}>
        <ringGeometry args={[baseRadius * 1.15, baseRadius * 1.4, 48]} />
        <meshBasicMaterial
          color={isSpeaking ? '#4a8a73' : '#3b725f'}
          transparent
          opacity={0}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* The bubble: body + gloss overlay billboard together as a group. */}
      <group
        ref={groupRef}
        position={node.position}
        onPointerOver={handleOver}
        onPointerOut={handleOut}
        onClick={handleClick}
      >
        <mesh>
          <circleGeometry args={[baseRadius, 48]} />
          <meshBasicMaterial
            map={texture}
            transparent
            opacity={opacity}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>
        {/* Gloss overlay — sits a hair in front so it composites over the
            body (color, photo, or video) and never causes z-fighting. */}
        <mesh position={[0, 0, baseRadius * 0.02]} raycast={() => null}>
          <circleGeometry args={[baseRadius, 48]} />
          <meshBasicMaterial
            map={glossTex}
            transparent
            opacity={opacity}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>
      </group>
    </>
  );
}

// Imperative zoom plumbing — adjusts camera distance from origin while
// preserving look direction. zoomToFit snaps back to default distance.
function CameraRig({ controlsRef, zoomSignal }) {
  const { camera } = useThree();
  useEffect(() => {
    if (!zoomSignal) return;
    const { kind } = zoomSignal;
    if (kind === 'in') {
      camera.position.setLength(Math.max(8, camera.position.length() * 0.8));
    } else if (kind === 'out') {
      camera.position.setLength(Math.min(40, camera.position.length() * 1.25));
    } else if (kind === 'fit') {
      const dir = camera.position.clone().normalize();
      camera.position.copy(dir.multiplyScalar(16));
    }
    if (controlsRef.current) controlsRef.current.update();
  }, [zoomSignal, camera, controlsRef]);
  return null;
}

function ClusterScene({ placed, hoveredId, setHoveredId, selectedMemory, highlightedMemoryIds, isSpeaking, searchMatchIds, onMemoryClick, controlsRef }) {
  // R3F event manager — suspended during orbit drags so pointermove doesn't
  // raycast against every bubble mesh mid-rotation.
  const { events, gl } = useThree();
  useEffect(() => {
    const el = gl.domElement;
    let down = false, dragging = false, dx = 0, dy = 0;
    const onDown = (e) => { down = true; dragging = false; dx = e.clientX; dy = e.clientY; };
    const onMove = (e) => {
      if (!down || dragging) return;
      if (Math.abs(e.clientX - dx) + Math.abs(e.clientY - dy) > 6) {
        dragging = true;
        events.enabled = false; // real drag — stop raycasting every bubble per pointermove
      }
    };
    const onUp = () => {
      down = false;
      if (dragging) setTimeout(() => { events.enabled = true; }, 0);
      dragging = false;
    };
    el.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      events.enabled = true;
    };
  }, [events, gl]);
  const highlightedIds = useMemo(
    () => new Set((highlightedMemoryIds || []).map(m => m.id)),
    [highlightedMemoryIds]
  );

  return (
    <>
      <OrbitControls
        ref={controlsRef}
        enableDamping
        dampingFactor={0.08}
        minDistance={6}
        maxDistance={32}
        autoRotate
        autoRotateSpeed={0.3}
        enablePan={false}
      />

      {placed.map(node => {
        const isAIHighlighted = highlightedIds.has(node.id);
        const isSearchMatch = searchMatchIds && searchMatchIds.has(node.id);
        const isDimmed =
          (searchMatchIds && !isSearchMatch) ||
          (highlightedIds.size > 0 && !isAIHighlighted);

        return (
          <MemoryBubble
            key={node.id}
            node={node}
            isSelected={selectedMemory && selectedMemory.id === node.id}
            isHovered={hoveredId === node.id}
            isHighlightedByAI={isAIHighlighted}
            isSpeaking={isSpeaking}
            isDimmed={isDimmed}
            onSelect={onMemoryClick}
            onHover={setHoveredId}
          />
        );
      })}
    </>
  );
}

const MemoryBubble3D = React.forwardRef(function MemoryBubble3D(
  { nodes, onMemoryClick, selectedMemory, highlightedMemoryIds = [], isSpeaking = false, searchMatchIds = null },
  ref
) {
  const [hoveredId, setHoveredId] = useState(null);
  const controlsRef = useRef();
  const [zoomSignal, setZoomSignal] = useState(null);

  const placed = useMemo(() => placeNodes(nodes || []), [nodes]);

  React.useImperativeHandle(ref, () => ({
    zoomIn: () => setZoomSignal({ kind: 'in', t: Date.now() }),
    zoomOut: () => setZoomSignal({ kind: 'out', t: Date.now() }),
    zoomToFit: () => setZoomSignal({ kind: 'fit', t: Date.now() }),
  }));

  if (!placed.length) return null;

  return (
    <Canvas
      camera={{ position: [0, 0, 16], fov: 50 }}
      gl={{ antialias: true, alpha: true }}
      dpr={[1, 2]}
      style={{ width: '100%', height: '100%', background: 'transparent' }}
    >
      <CameraRig controlsRef={controlsRef} zoomSignal={zoomSignal} />
      <ClusterScene
        placed={placed}
        hoveredId={hoveredId}
        setHoveredId={setHoveredId}
        selectedMemory={selectedMemory}
        highlightedMemoryIds={highlightedMemoryIds}
        isSpeaking={isSpeaking}
        searchMatchIds={searchMatchIds}
        onMemoryClick={onMemoryClick}
        controlsRef={controlsRef}
      />
    </Canvas>
  );
});

export default MemoryBubble3D;
